import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { useAppOptionValueEquals } from "../../../hooks/framework/useAppOptionValue";
import { OptionOwnerHasTransportAttachmentsEnabled } from "../../../models/common/consts";
import { useAppDispatch, useAppSelector } from "../../../framework/redux/hooks";
import { Alert, Fab, IconButton, List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { Download, FilePresent, InfoOutlined } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import { addAttachmentsForTransportOrder, deleteTransportOrderAttachment, downloadAttachmentForTransportOrder, getAttachmentsForTransportOrder } from "../../../services/transportOrderService";
import { ConfirmType, useConfirm } from "../../../hooks/framework/useConfirm";
import { handleApiError } from "../../../services/baseService";
import { addErrorMsg, addSuccessMsg } from "../../../framework/redux/slices/appSlice";
import { AttachmentListItemDto } from "../../../models/attachment/attachment";
import mergeFileLists from "../../../models/common/mergeFileLists";
import AddIcon from "@mui/icons-material/Add";
import { LoadingButton } from "../../framework/loadingButton";
import InfoIcon from "@mui/icons-material/Info";

const fileSizeLimit = 1024000 * 10; // 10 MB limit
const acceptedFileTypes = "image/png, image/jpeg, application/pdf";

export interface OrderAttachmentsProps {
    employeeId: string;
}

export const OrderAttachments = ({ employeeId } : OrderAttachmentsProps) => {
    const { t } = useTranslation();
    const [isUploading, setIsUploading] = useState(false);
    const dispatch = useAppDispatch();
    const confirm = useConfirm();
    const order = useAppSelector((state) => state.transportOrderDetails.order);
    const [transportOrderFiles, setTransportOrderFiles] = useState<AttachmentListItemDto[]>(null);
    const [fileFormData, setFileFormData] = useState<FormData>(null);
    const [files, setFiles] = useState<FileList>(null);
    const [fileNames, setFileNames] = useState<string[]>(null);
    const [hasInvalidFiles, setHasInvalidFiles] = useState(false);
    const [isInfoBoxOpen, setIsInfoBoxOpen] = useState(false);

    const [ownerHasTransportAttachmentsEnabled] = useAppOptionValueEquals(
        OptionOwnerHasTransportAttachmentsEnabled,
        true
    );

    const fetchOrderFiles = async() => {
        try {
            const res = await getAttachmentsForTransportOrder(order.id);
            if (res?.data) {
                setTransportOrderFiles(res.data);
            }
        } catch (error) {
            dispatch(addErrorMsg({ message: t("transport.orderAttachments.fetchFailed") }));
        }
    };

    useEffect(() => {
        if (order?.id) {
            setTransportOrderFiles(order?.attachments);
        }
    }, [order]);

    const handleFileAdd = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }
        setHasInvalidFiles(false);

        const formData = new FormData();
        const newFileNames = [];

        let newFileList = e.target.files;
        if (files?.length > 0) {
            newFileList = mergeFileLists(files, e.target.files);
        }

        let i = 0;
        while (i < newFileList.length) {
            // Check that file size and file type are allowed
            if (newFileList[i].size < fileSizeLimit &&
                acceptedFileTypes.includes(newFileList[i].type)
            ) {
                const file = newFileList[i];
                newFileNames.push(file.name);
                formData.append("files", file);
            } else {
                setHasInvalidFiles(true);
            }
            i++;
        }
        // Set updated files and form data
        setFiles(newFileList);
        setFileNames(newFileNames);
        setFileFormData(formData);
    };

    const handleFilesToUploadDelete = (fileIndex: number) => {
        const updatedFiles = new DataTransfer();
        const formData = new FormData();
        const fileNames = [];
        let i = 0;
        while (i < files.length) {
            if (i !== fileIndex) {
                const file = files[i];
                updatedFiles.items.add(file);
                fileNames.push(file.name);
                formData.append("files", file);
            }
            i++;
        }
        setFiles(updatedFiles.files);
        setFileNames(fileNames);
        setFileFormData(formData);
    };

    const handleFileDownload = (file: AttachmentListItemDto) => {
        downloadAttachmentForTransportOrder(order.id, file.attachmentId)
            .then(res => {
                console.log(res);
                const data = new Blob([res.data], { type: file.fileType });
                const fileURL = window.URL.createObjectURL(data);
                const tempLink = document.createElement("a");
                tempLink.href = fileURL;
                tempLink.setAttribute("download", file.fileName);
                tempLink.click();
            })
            .catch(handleApiError);
    };

    const handleFileUploadAction = () => {
        setIsUploading(true);
        void handleFileUpload();
    };
    const handleFileUpload = () => {
        setIsUploading(true);
        addAttachmentsForTransportOrder(fileFormData, order.id)
            .then(() => {
                dispatch(
                    addSuccessMsg(
                        t("transport.orderAttachments.orderAttachmentAdded")
                    )
                );
                void fetchOrderFiles();
                setHasInvalidFiles(false);
                setFiles(null);
                setFileNames(null);
                setFileFormData(null);
            })
            .catch(handleApiError)
            .finally(() => {
                setIsUploading(false);
            });
    };

    const handleFileDelete = (attachmentId: string, fileName: string) => {
        confirm({
            icon: DeleteIcon,
            title: t("transport.orderAttachments.deleteOrderAttachment", { fileName: fileName }),
            submitText: t("common.continue"),
            cancelText: t("common.cancel"),
            type: ConfirmType.Remove,
            onSubmit: () => {
                deleteTransportOrderAttachment(order.id, attachmentId)
                    .then(() => {
                        void fetchOrderFiles();
                        dispatch(
                            addSuccessMsg(
                                t("transport.orderAttachments.orderAttachmentDeleted", { fileName: fileName })
                            )
                        );
                    })
                    .catch(handleApiError);
            },
        });
    };

    const canDeleteFile = (createdEmployeeId: string): boolean => {
        if (createdEmployeeId !== employeeId) return false;

        return true;
    };

    if (!ownerHasTransportAttachmentsEnabled) {
        return null;
    }

    return (
        <Box mx={2} pt={2}>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "primary.dark" }}>
                { t("common.attachments") }
                <IconButton
                    color="primary"
                    onClick={() => {setIsInfoBoxOpen(!isInfoBoxOpen);}}
                    size="small"
                    sx={{ ml: 1 }}
                >
                    <InfoIcon />
                </IconButton>
            </Typography>
            {isInfoBoxOpen && (
                <Box marginTop={1}>
                    <Alert icon={false} severity="info" onClose={() => {setIsInfoBoxOpen(false);}}>
                        <List dense>
                            <ListItem>
                                <ListItemIcon>
                                    <InfoOutlined />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t("common.allowedFileFormats", { formats: "JPG, PNG, PDF" })}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <InfoOutlined />
                                </ListItemIcon>
                                <ListItemText
                                    primary={t("common.maxFileSize", { size: "10 MB" })}
                                />
                            </ListItem>
                        </List>
                    </Alert>
                </Box>
            )}
            <Box
                sx={{
                    flexDirection: "column",
                    p: transportOrderFiles?.length > 0 ? 0 : 2,
                }}
            >
                {transportOrderFiles && transportOrderFiles.length > 0 ? (
                    <List>
                        {transportOrderFiles.map((file, index) =>
                            <ListItem
                                key={index}
                                secondaryAction={
                                    <>
                                        <IconButton
                                            onClick={() => void handleFileDownload(file)}
                                            edge="end"
                                            aria-label="download"
                                        >
                                            <Download color="primary" />
                                        </IconButton>
                                        {canDeleteFile(file.createdEmployeeId) && (
                                            <IconButton
                                                onClick={() => void handleFileDelete(file.attachmentId, file.fileName)}
                                                edge="end"
                                                aria-label="delete"
                                                sx={{ marginLeft: "18px" }}
                                            >
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        )}
                                    </>
                                }
                            >
                                <ListItemIcon style={{ minWidth: "34px" }}>
                                    <FilePresent />
                                </ListItemIcon>
                                <ListItemText
                                    disableTypography
                                    primary={
                                        <Typography variant="body2" style={{
                                            maxWidth: "calc(100% - 50px)",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis"
                                        }}
                                        >
                                            {file.fileName}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        )}
                    </List>
                ) : (
                    <Typography variant="body2">
                        {t("transport.orderAttachments.noAttachments")}
                    </Typography>
                )}
            </Box>
            <Box sx={{ mb: 2 }}>
                <Fab
                    data-testid={"attachment-add"}
                    color="primary"
                    size="small"
                    aria-label="attachment-add"
                    sx={{ overflow: "hidden" }}
                >
                    <input
                        type="file"
                        onChange={handleFileAdd}
                        accept={acceptedFileTypes}
                        multiple
                        style={{
                            //make this hidden and display only the icon
                            position: "absolute",
                            top: "-35px",
                            left: 0,
                            height: "calc(100% + 36px)",
                            width: "calc(100% + 5px)",
                            outline: "none",
                        }}
                    />
                    <AddIcon />
                </Fab>
                {((fileNames && fileNames.length > 0) || hasInvalidFiles) && (
                    <Box>
                        <Box>
                            <List>
                                {fileNames?.map((file, index) =>
                                    <ListItem
                                        key={index}
                                        secondaryAction={
                                            <IconButton
                                                onClick={() => void handleFilesToUploadDelete(index)}
                                                edge="end"
                                                aria-label="delete"
                                            >
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIcon>
                                            <FilePresent />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={file}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                        {hasInvalidFiles && (
                            <Box marginY={2}>
                                <Alert severity="error" onClose={() => {setHasInvalidFiles(false);}}>
                                    {t("common.attachmentFileInvalid")}
                                </Alert>
                            </Box>
                        )}
                        <LoadingButton
                            color="success"
                            variant="contained"
                            disabled={isUploading}
                            loading={isUploading}
                            onClick={() => void handleFileUploadAction()}
                        >
                            {t("common.save")}
                        </LoadingButton>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
